import {
  api as generatedApi,
  CreateDispatchTicketApiArg,
  DispatchTicketSchema,
  PatchDispatchTicketApiArg,
  PatchDispatchTicketBody,
  PatchMultipleDispatchTicketsApiArg,
  UpdateDispatchTicketApiArg,
  VoidDispatchTicketApiArg,
} from 'gen/rtk-openapi'
import { ASSET_CATEGORY } from 'utils/constants'
import { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions'

const TAG_TYPES = {
  dispatchTickets: 'dispatchTickets',
  container: ASSET_CATEGORY.CONTAINER,
  truck: ASSET_CATEGORY.TRUCK,
  trailer: ASSET_CATEGORY.TRAILER,
  // only use for physical assets by id requests
  physicalAsset: 'PHYSICAL_ASSET',
  inboundTickets: 'InboundTickets',
  outboundTickets: 'OutboundTickets',
  recurringDispatchTickets: 'recurringDispatchTickets',
}

/**
 * The actual rest api backend doesn't accept an array of assetIds
 * this interface lets the client provide an array of assetIds that will be effected by the dispatch ticket patch so we can also invalidate the tags for the asset statuses
 */
export interface PatchDispatchTicketWithTagMetadata extends PatchDispatchTicketApiArg {
  assetIds: string[]
  inboundTicketId?: string
  outboundTicketId?: string
}

export const api = generatedApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPES.inboundTickets, TAG_TYPES.dispatchTickets],
  endpoints: {
    getDispatchTickets: {
      providesTags: result =>
        result && result.Items
          ? // successful query
            [
              ...result.Items.map(dispatchTicket => ({
                type: TAG_TYPES.dispatchTickets,
                id: dispatchTicket.id,
              })),
              { type: TAG_TYPES.dispatchTickets, id: 'LIST' },
            ]
          : // error occurred, refetch when `{ type: TAG_TYPES.dispatchTickets, id: 'LIST' }` is invalidated
            [{ type: TAG_TYPES.dispatchTickets, id: 'LIST' }],
    },
    getDispatchTicket: {
      providesTags: (_res, _err, { dispatchTicketId }) => [{ type: TAG_TYPES.dispatchTickets, id: dispatchTicketId }],
    },
    patchMultipleDispatchTickets: {
      invalidatesTags: (_res, _err, patchArgs: PatchMultipleDispatchTicketsApiArg): TagDescription<string>[] => {
        return patchArgs.body.map((body: PatchDispatchTicketBody) => ({
          type: TAG_TYPES.dispatchTickets,
          id: body.id,
        }))
      },
    },
    createDispatchTicket: {
      invalidatesTags: (_res, _err, createQueryArgs: CreateDispatchTicketApiArg) => {
        const assetStatusTags = createQueryArgs.createDispatchTicketBody.dispatchTicketAssets?.map(asset => ({
          type: TAG_TYPES.physicalAsset,
          id: asset.assetId,
        }))
        return [
          { type: TAG_TYPES.dispatchTickets, id: 'LIST' },
          { type: TAG_TYPES.container, id: 'LIST' },
          { type: TAG_TYPES.truck, id: 'LIST' },
          { type: TAG_TYPES.trailer, id: 'LIST' },
          ...(assetStatusTags || []),
        ]
      },
    },
    updateDispatchTicket: {
      invalidatesTags: (_res, _err, updateQueryArgs: UpdateDispatchTicketApiArg) => {
        const assetStatusTags = updateQueryArgs.updateDispatchTicketBody.dispatchTicketAssets?.map(asset => ({
          type: TAG_TYPES.physicalAsset,
          id: asset.assetId,
        }))
        return [
          { type: TAG_TYPES.dispatchTickets, id: updateQueryArgs.dispatchTicketId },
          { type: TAG_TYPES.dispatchTickets, id: 'LIST' },
          { type: TAG_TYPES.container, id: 'LIST' },
          { type: TAG_TYPES.truck, id: 'LIST' },
          { type: TAG_TYPES.trailer, id: 'LIST' },
          ...(assetStatusTags || []),
        ]
      },
    },
    patchDispatchTicket: {
      // when we invalidate tags for dispatch ticket, we also need to invalidate tags for the associated assets
      invalidatesTags: (_res: DispatchTicketSchema | undefined, _err, patchQueryArgs: PatchDispatchTicketApiArg) => {
        const assetStatusTags = (patchQueryArgs as PatchDispatchTicketWithTagMetadata).assetIds.map(assetId => ({
          type: TAG_TYPES.physicalAsset,
          id: assetId,
        }))
        return [
          { type: TAG_TYPES.dispatchTickets, id: patchQueryArgs.dispatchTicketId },
          { type: TAG_TYPES.dispatchTickets, id: 'LIST' },
          { type: TAG_TYPES.container, id: 'LIST' },
          { type: TAG_TYPES.truck, id: 'LIST' },
          { type: TAG_TYPES.trailer, id: 'LIST' },
          {
            type: TAG_TYPES.inboundTickets,
            id: (patchQueryArgs as PatchDispatchTicketWithTagMetadata).inboundTicketId,
          },
          {
            type: TAG_TYPES.outboundTickets,
            id: (patchQueryArgs as PatchDispatchTicketWithTagMetadata).outboundTicketId,
          },
          ...(assetStatusTags || []),
        ]
      },
    },
    voidDispatchTicket: {
      invalidatesTags: (_res: DispatchTicketSchema | undefined, _err, voidQueryArgs: VoidDispatchTicketApiArg) => {
        const assetStatusTags = voidQueryArgs.voidDispatchTicketBody.dispatchTicketAssets?.map(asset => ({
          type: TAG_TYPES.physicalAsset,
          id: asset.id,
        }))
        return [
          { type: TAG_TYPES.dispatchTickets, id: voidQueryArgs.dispatchTicketId },
          { type: TAG_TYPES.dispatchTickets, id: 'LIST' },
          { type: TAG_TYPES.container, id: 'LIST' },
          { type: TAG_TYPES.truck, id: 'LIST' },
          { type: TAG_TYPES.trailer, id: 'LIST' },
          ...(assetStatusTags || []),
        ]
      },
    },
    createRecurringDispatchTicket: {
      invalidatesTags: (_res, _err) => [{ type: TAG_TYPES.dispatchTickets, id: 'LIST' }],
    },
    archiveRecurringDispatchTicket: {
      invalidatesTags: (_res, _err) => [{ type: TAG_TYPES.dispatchTickets, id: 'LIST' }],
    },
  },
})

export const {
  useGetDispatchTicketsQuery,
  useGetDispatchTicketQuery,
  usePatchMultipleDispatchTicketsMutation,
  useCreateDispatchTicketMutation,
  useUpdateDispatchTicketMutation,
  usePatchDispatchTicketMutation,
  useVoidDispatchTicketMutation,
  useCreateRecurringDispatchTicketMutation,
  useArchiveRecurringDispatchTicketMutation,
} = api
